// Three.js scene boot: renderer, scene, ortho camera, lights, resize handling.
// Canvas size is read from its parent container (not window) so the scene fits
// correctly inside the PLAY page section when the site shell is present.

import * as THREE from 'three'

export function createScene({ canvas }) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.setClearColor(0x000000, 0)

  const scene = new THREE.Scene()

  const frustum = 8
  const { w: initW, h: initH } = canvasSize(canvas)
  const aspect = initW / Math.max(1, initH)
  const camera = new THREE.OrthographicCamera(
    (-frustum * aspect) / 2,
    (frustum * aspect) / 2,
    frustum / 2,
    -frustum / 2,
    0.1,
    100,
  )
  camera.position.set(10, 10, 10)
  camera.lookAt(0, 0, 0)

  // Ambient + sun start at neutral daytime values; the day/night controller
  // (dayNight.js) animates their color/intensity/position from local time.
  const ambient = new THREE.AmbientLight(0xfff3d6, 0.55)
  scene.add(ambient)

  const sun = new THREE.DirectionalLight(0xfff0c4, 0.9)
  sun.position.set(6, 10, 4)
  scene.add(sun)

  const doResize = () => resize({ renderer, camera, frustum, canvas })
  doResize()
  window.addEventListener('resize', doResize)

  // The canvas's parent may also resize without a window event (page swap,
  // header reflow on mobile). A ResizeObserver covers that.
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => doResize())
    ro.observe(canvas.parentElement ?? canvas)
  }

  return { renderer, scene, camera, ambient, sun }
}

function canvasSize(canvas) {
  const parent = canvas.parentElement
  if (parent) return { w: parent.clientWidth, h: parent.clientHeight }
  return { w: window.innerWidth, h: window.innerHeight }
}

function resize({ renderer, camera, frustum, canvas }) {
  const { w, h } = canvasSize(canvas)
  if (w === 0 || h === 0) return // page is hidden
  const aspect = w / h

  camera.left = (-frustum * aspect) / 2
  camera.right = (frustum * aspect) / 2
  camera.top = frustum / 2
  camera.bottom = -frustum / 2
  camera.updateProjectionMatrix()

  renderer.setSize(w, h, false)
}
